import {Hotkey} from '@wandb/components';
// @todo: Migrate to 'SkipToContent' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  Content as DropdownContent,
  Item as DropdownItem,
  Portal as DropdownPortal,
  Root as DropdownRoot,
  Separator as DropdownSeparator,
  Trigger as DropdownTrigger,
} from '@wandb/weave/components/DropdownMenu';
// @todo: Migrate to 'Switch' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import * as Switch from '@wandb/weave/components/Switch';
import React, {memo, useCallback, useEffect, useState} from 'react';

import {useIsInLoadedReportContext} from '../pages/ReportPage/LoadedReportContext';
import {
  trackRunVisibilityMenu,
  type VisibilityMenuAction,
} from '../services/analytics/runSelectAllVisibilityMenuEvents';
import {hotkeyPressed} from '../util/hotkeys';
import {MultiStateCheckbox} from './MultiStateCheckbox';
import {useRunSelectorAnalyticsLocation} from './RunSelector/RunSelectorAnalyticsLocationContext';

interface RunSelectAllProps {
  allCheckedState: 'unchecked' | 'checked' | 'partial' | 'unknown';
  onlyShowSelected?: boolean;
  setOnlyShowSelected?(newSetting: boolean): void;
  selectAllVisible(): void; // copies visualized runs to temp selections
  onSelectAll(): void;
  onSelectNone(): void;
  onSelectVisibleRuns(): void;
}

const RunSelectAllComp: React.FC<RunSelectAllProps> = ({
  allCheckedState,
  onSelectAll,
  onSelectNone,
  onSelectVisibleRuns,
  onlyShowSelected,
  setOnlyShowSelected,
  selectAllVisible,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const isInReport = useIsInLoadedReportContext();
  const trackVisibilityMenuWithLocation = useCallback(
    (action: VisibilityMenuAction, method: 'hotkey' | 'dropdown') => {
      trackRunVisibilityMenu({action, method, location: analyticsLocation});
    },
    [analyticsLocation]
  );

  const handleHotkeyToggleVisualizedRuns = useCallback(
    (e: KeyboardEvent) => {
      if (
        e.key &&
        hotkeyPressed(e, 'toggleVisualizedRuns') &&
        setOnlyShowSelected &&
        !isInReport
      ) {
        trackVisibilityMenuWithLocation(
          'Only show visualized toggled',
          'hotkey'
        );
        setOnlyShowSelected(!onlyShowSelected);
      }
    },
    [
      onlyShowSelected,
      setOnlyShowSelected,
      trackVisibilityMenuWithLocation,
      isInReport,
    ]
  );

  useEffect(() => {
    if (isInReport) {
      return;
    }
    document.addEventListener('keydown', handleHotkeyToggleVisualizedRuns);
    return () => {
      document.removeEventListener('keydown', handleHotkeyToggleVisualizedRuns);
    };
  }, [handleHotkeyToggleVisualizedRuns, isInReport]);

  return (
    <DropdownRoot
      open={isMenuOpen}
      onOpenChange={(open: boolean) => {
        if (open) {
          trackVisibilityMenuWithLocation('Visibility menu opened', 'dropdown');
        }
        setIsMenuOpen(open);
      }}>
      <DropdownTrigger>
        <button type="button" style={{all: 'unset', cursor: 'pointer'}}>
          <MultiStateCheckbox
            data-test="all-runs-visibility-checkbox"
            aria-label="Visibility options"
            checked={allCheckedState}
            theme="visibility"
          />
        </button>
      </DropdownTrigger>
      <DropdownPortal>
        <DropdownContent
          align="start"
          sideOffset={8}
          alignOffset={0}
          className="z-[1]">
          <DropdownItem
            onSelect={() => {
              onSelectAll();
              trackVisibilityMenuWithLocation('Make all visible', 'dropdown');
            }}>
            Make all visible
          </DropdownItem>
          <DropdownItem
            onSelect={() => {
              onSelectNone();
              trackVisibilityMenuWithLocation('Make all hidden', 'dropdown');
            }}>
            Make all hidden
          </DropdownItem>
          <DropdownSeparator />
          <DropdownItem
            onSelect={e => {
              e.preventDefault();
              if (setOnlyShowSelected) {
                setOnlyShowSelected(!onlyShowSelected);
              }
              trackVisibilityMenuWithLocation(
                'Only show visualized toggled',
                'dropdown'
              );
            }}>
            <span className="pr-10">Only show visualized</span>
            {!isInReport && (
              <Hotkey keys={['Alt', 'KeyV']} name="Only show visualized" />
            )}
            <Switch.Root checked={onlyShowSelected}>
              <Switch.Thumb checked={onlyShowSelected ? true : false} />
            </Switch.Root>
          </DropdownItem>
          <DropdownItem
            onSelect={() => {
              selectAllVisible();
              trackVisibilityMenuWithLocation(
                'Select visible runs',
                'dropdown'
              );
              onSelectVisibleRuns();
            }}>
            Select visible runs
          </DropdownItem>
        </DropdownContent>
      </DropdownPortal>
    </DropdownRoot>
  );
};

export const RunSelectAll = memo(RunSelectAllComp);
